import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import { addToast } from 'components/Toast/Toast'
import { SwitchButton } from 'components-ref/atoms'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { putChatSettings, putRemoveDuplicates  } from 'services/endpoints/settings/general/chat'
import { useAuthStore, useUserProfile } from 'store'
import screens from 'styles/screens'

import { SSAccordion } from '../SSAccordion'

export const Chat = () => {
    const userData = useUserProfile()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const removeDuplicatesMutation = useMutation(
        async (value: boolean) => await putRemoveDuplicates({ remove_duplicates: value }),
        {
            onMutate: async (value: boolean) => {
                const previousProfile = useAuthStore.getState().userProfile

                if (previousProfile) {
                    useAuthStore.getState().setUserProfile({
                        remove_duplicates: value
                    })
                }

                return { previousProfile }
            },
            onError: (error, _value, context: any) => {
                console.error(error)

                if (context?.previousProfile) {
                    useAuthStore.getState().setUserProfile(context.previousProfile)
                }

                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    const chatSettingsMutation = useMutation(
        async (params: Record<string, boolean | string>) =>
            await putChatSettings(params),
        {
            onMutate: async (newData: Record<string, boolean | string>) => {
                await queryClient.fetchQuery('user-profile')
                const previousQueryData =
                    queryClient.getQueryData('user-profile')
                queryClient.setQueryData('user-profile', (oldData: any) => {
                    return {
                        ...oldData,
                        chat_settings: {
                            ...oldData.chat_settings,
                            ...newData
                        }
                    }
                })
                return { previousQueryData }
            },
            onError: (error, _, context: any) => {
                console.error(error)
                queryClient.setQueryData(
                    'user-profile',
                    context.previousQueryData
                )
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    return (
        <Container>
            {userData?.role === 'model' && (
                <>
                    <ActionCard
                        text={t('welcomeMessage')}
                        description={t(
                            'setUpAnAutoMessageToYourNewSubscribers'
                        )}
                        hasSwitch
                        toggleActive={
                            userData?.chat_settings?.welcome_message_enabled
                        }
                        toggleFn={() => {
                            chatSettingsMutation.mutate({
                                welcome_message_enabled:
                                    !userData?.chat_settings
                                        ?.welcome_message_enabled
                            })
                        }}
                        link='/settings/general/chat-settings/welcome-message'
                        hasArrow
                    />
{/*                     <ActionCard
                        text={t('chatStrategy')}
                        description={t('chatStrategyDetail')}
                        hasSwitch
                        toggleActive={userData?.remove_duplicates || false}
                        toggleFn={() => {
                            removeDuplicatesMutation.mutate(!userData?.remove_duplicates)
                        }}
                        link='/settings/general/chat-settings/chat-strategy'
                        hasArrow
                    /> */}
                    <ActionCard
                        text={t('duplicateMedia')}
                        description={t('removeDuplicateRecipients')}
                        hasSwitch
                        toggleActive={userData?.remove_duplicates || false}
                        toggleFn={() => {
                            removeDuplicatesMutation.mutate(!userData?.remove_duplicates)
                        }}
                    />
                    <SSAccordion
                        expanded={true}
                        customClass='chat-ssacrodion'
                        head={
                            <>
                                <span>Premium Videos</span>
                            </>
                        }
                        items={[
                            {
                                text: 'No Preview',
                                active:
                                    userData?.chat_settings
                                        ?.video_preview_type === 'no-preview',
                                toggle: () => {
                                    chatSettingsMutation.mutate({
                                        video_preview_type: 'no-preview'
                                    })
                                }
                            },
                            {
                                text: 'Picture Preview',
                                active:
                                    userData?.chat_settings
                                        ?.video_preview_type ===
                                    'picture-preview',
                                toggle: () => {
                                    chatSettingsMutation.mutate({
                                        video_preview_type: 'picture-preview'
                                    })
                                }
                            },
                            {
                                text: 'Video Preview',
                                active:
                                    userData?.chat_settings
                                        ?.video_preview_type ===
                                    'video-preview',
                                toggle: () => {
                                    chatSettingsMutation.mutate({
                                        video_preview_type: 'video-preview'
                                    })
                                }
                            }
                        ]}
                    />
                </>
            )}
            <SSAccordion
                expanded={userData?.chat_settings?.favorite_contacts || false}
                customClass={`chat-ssacrodion chat-ssacrodion-close ${
                    userData?.chat_settings?.favorite_contacts &&
                    'chat-ssacrodion-open'
                }`}
                customExpandClass={true}
                head={
                    <>
                        <span>{t('favoriteContacts')}</span>
                        <SwitchButton
                            active={
                                userData?.chat_settings?.favorite_contacts ||
                                false
                            }
                            toggle={() => {
                                chatSettingsMutation.mutate({
                                    favorite_contacts:
                                        !userData?.chat_settings
                                            ?.favorite_contacts
                                })
                            }}
                        />
                    </>
                }
                items={[
                    {
                        text: t('mostMessages'),
                        active: userData?.chat_settings?.most_messages || false,
                        toggle: () => {
                            chatSettingsMutation.mutate({
                                most_messages: true,
                                most_messages_purchases: false,
                                latest_subscribed: false,
                                online: false
                            })
                        }
                    },
                    {
                        text: t('mostMessagePurchases'),
                        active:
                            userData?.chat_settings?.most_messages_purchases ||
                            false,
                        toggle: () => {
                            chatSettingsMutation.mutate({
                                most_messages: false,
                                most_messages_purchases: true,
                                latest_subscribed: false,
                                online: false
                            })
                        }
                    },
                    {
                        text: t('latestSubscribed'),
                        active:
                            userData?.chat_settings?.latest_subscribed || false,
                        toggle: () => {
                            chatSettingsMutation.mutate({
                                most_messages: false,
                                most_messages_purchases: false,
                                latest_subscribed: true,
                                online: false
                            })
                        }
                    },
                    {
                        text: t('online'),
                        active: userData?.chat_settings?.online || false,
                        toggle: () => {
                            chatSettingsMutation.mutate({
                                most_messages: false,
                                most_messages_purchases: false,
                                latest_subscribed: false,
                                online: true
                            })
                        }
                    }
                ]}
            />
        </Container>
    )
}

const Container = styled.div`
    @media (max-width: ${screens.medium}) {
        // padding-bottom: 7.5rem;
    }
`
